import { parseKeyX, parseKeyY, parseKeyZ } from '@/voxelMath'
export { occupancyFromSurface, type Occupancy2D, nearestOccupancyHit } from '@/core/occupancy'
import type { Node, SceneDoc } from '@/dsl/types'
import { compileSceneDsl } from '@/dsl/compile'
import { blendMutable } from '@/events/layerSystem'
import type { VoxelMap, VoxelData } from '@/voxelTypes'

export type Surface =
  | { kind: 'ascii';
      // ASCII art lines describing occupancy. Each line is a row along +Z; columns advance +X.
      // Non-dot, non-whitespace characters are considered occupied by default.
      lines: string | ReadonlyArray<string>;
      // Optional placement offset (x,z) for the ASCII art origin within bounds.
      at?: readonly [number, number];
      // Optional string of characters to treat as 'occupied' (overrides default rule).
      occupied?: string;
      padding?: Padding;
    }
  | { kind: 'scene';
      // Scene authored with the same DSL as normal scenes; projected to a 2D mask.
      scene: SceneDoc | Node | ReadonlyArray<Node>;
      // In-plane axes: default 'xz'.
      plane?: 'xz' | 'xy' | 'yz';
      padding?: Padding;
    }

export type Padding = { top: number; right: number; bottom: number; left: number }
export type PaddingSpec = number | Padding

export function normalizePadding(spec?: PaddingSpec): Padding {
  if (spec == null) return { top: 4, right: 4, bottom: 4, left: 4 }
  if (typeof spec === 'number') {
    const n = Math.max(0, Math.floor(spec))
    return { top: n, right: n, bottom: n, left: n }
  }
  const top = Math.max(0, Math.floor(spec.top ?? 0))
  const right = Math.max(0, Math.floor(spec.right ?? 0))
  const bottom = Math.max(0, Math.floor(spec.bottom ?? 0))
  const left = Math.max(0, Math.floor(spec.left ?? 0))
  return { top, right, bottom, left }
}

// Compute raw XZ extents of a surface before padding
export function computeSurfaceExtents(surface: Surface): { width: number; height: number } {
  if (surface.kind === 'ascii') {
    const lines: ReadonlyArray<string> = Array.isArray(surface.lines)
      ? surface.lines as ReadonlyArray<string>
      : String(surface.lines).split(/\r?\n/)
    let width = 0
    for (const line of lines) width = Math.max(width, (line ?? '').length)
    const height = lines.length
    return { width, height }
  }
  if (surface.kind === 'scene') {
    const acc: VoxelMap = new Map<number, VoxelData>()
    const { events } = compileSceneDsl(surface.scene)
    for (const ev of events) {
      if (ev[0] !== 'voxel.blend') continue
      const [, voxels, mode] = ev as any
      blendMutable(acc, voxels as any, mode as any)
    }
    let minI = Infinity, maxI = -Infinity
    let minJ = Infinity, maxJ = -Infinity
    const plane = surface.plane || 'xz'
    ;(acc as any).forEach?.((_: VoxelData, key: number) => {
      const x = parseKeyX(key), y = parseKeyY(key), z = parseKeyZ(key)
      let i = 0, j = 0
      if (plane === 'xz') { i = x; j = z }
      else if (plane === 'xy') { i = x; j = y }
      else { i = y; j = z }
      if (i < minI) minI = i; if (i > maxI) maxI = i
      if (j < minJ) minJ = j; if (j > maxJ) maxJ = j
    })
    if (minI === Infinity) return { width: 0, height: 0 }
    return { width: (maxI - minI + 1), height: (maxJ - minJ + 1) }
  }
  // Default: empty
  return { width: 0, height: 0 }
}

export function computeBoundsFromSurface(
  surface: Surface,
  y?: number
): { x: number; y: number; z: number } {
  const pad = normalizePadding((surface as any).padding as PaddingSpec | undefined)
  const { top, right, bottom, left } = pad

  const { width, height } = computeSurfaceExtents(surface)
  const x = Math.max(1, Math.ceil(width + left + right))
  const z = Math.max(1, Math.ceil(height + top + bottom))
  const computedY = y ?? Math.max(x, z)
  return { x, y: computedY, z: Math.max(1, z) }
}
